<script lang="ts">
	import { format } from 'svelte-i18n';
	import { afterUpdate, beforeUpdate, onDestroy } from 'svelte';
	import type { Device } from 'src/types/Device';
	import { os } from 'src/stores/session/native-bridge';
	import { getDeviceSettings, getDeviceStatus } from 'src/stores/device-runtime/status-selectors';
	import { navigateToSetting } from 'src/stores/ui/navigate-to';
	import DeviceNotificationLabel from 'src/components/Sidepanel/DeviceNotificationLabel.svelte';
	import DeviceViewStateWrapper from 'src/components/Sidepanel/DeviceViews/DeviceViewStateWrapper.svelte';
	import BluTrvBluetoothDisableAlert from 'src/components/DevicesSettings/VirtualComponents/BluTRV/BluTrvBluetoothDisableAlert.svelte';
	import TimerRemaining from 'src/components/TimerRemaining/TimerRemaining.svelte';
	import NextScheduleCard from 'src/components/Sidepanel/DeviceViews/NextScheduleCard.svelte';
	import TrvValveCommandGroup from 'src/components/Sidepanel/DeviceViews/Trv/TrvValveCommandGroup.svelte';
	import {
		Alert,
		ControlsContainer,
		DeviceButtonV2,
		IconButton,
		PriorityGrid,
		PropertyCard,
		PropertyDock,
		Text,
		TrvControl,
		capturePropertyCardFlip,
		playPropertyCardFlip,
	} from '@Shelly/shelly-design-system';
	import { getWorkingModeIcon } from 'src/helpers/device_data_helpers';
	import {
		celsius_to_fahrenheit,
		defaultTempFavPositions,
		fahrenheit_to_celsius,
		get_gen_2_component_channel_structure,
		get_gen_2_control_component_channel_structure,
		is_gen_2,
		real_id,
	} from 'src/helpers/interface_helpers';
	import { hapticFeedback } from 'src/services/native_container_requests_ios';
	import { capFirstChar, convertTime } from 'src/helpers/utilities';
	import { createSettlingRequestProxy } from 'src/helpers/requests/settling_request_proxy';
	import {
		TRV_OVERLAY_COPY,
		buildTrvPriorityLayout,
		trvPriorityFlipKey,
		type TrvPriorityLabel,
	} from 'src/helpers/trv_priority_layout';
	import {
		TRV_VALVE_SETTLE_TIMEOUT_MS,
		TRV_VALVE_SETTLED_DELTA,
		isTrvValveSettled,
		liveMatchesTrvDialValue,
		shouldClearTrvValvePending,
		shouldRevertTrvDialOptimistic,
	} from 'src/helpers/trv_valve_command_state';
	import {
		callBluTRV,
		getTrvComponentByDevice,
		isBluTRVCalibrated,
	} from 'src/helpers/virtual_helpers/blutrv_helpers';

	export let device: Device;

	const resolveLabel = (label: TrvPriorityLabel): string =>
		capFirstChar(
			$format(label.key, {
				default: label.default,
			}),
		);

	$: statusStore = getDeviceStatus(device?.id);
	$: status = $statusStore;
	$: settingsStore = getDeviceSettings(device?.id);
	$: settings = $settingsStore;
	$: trv = (() => {
		void status;
		void settings;
		return getTrvComponentByDevice(device);
	})();
	$: trvStatus = trv?.status?.['trv:0'];
	$: enableTrv = Boolean(trv?.config?.['trv:0']?.enable);
	$: unit = device?.ht_ui?.temperature_unit || 'C';
	$: currentC = trvStatus?.current_C;
	$: targetC = trvStatus?.target_C;
	$: position = trvStatus?.pos;
	$: battery = trv?.status?.battery;
	$: boost = Boolean(trvStatus?.boost);
	$: calibrated = isBluTRVCalibrated(device);
	$: layout = buildTrvPriorityLayout({
		enableTrv,
		online: Boolean(status?.online),
		currentC,
		targetC,
		position,
		battery,
		boost,
		temperatureUnit: unit,
		calibrated,
	});
	$: overlayCopy = layout.overlay ? TRV_OVERLAY_COPY[layout.overlay] : null;
	$: overlayCta = overlayCopy?.cta;
	$: notificationExcludes = layout.overlay
		? (['bleDisabled', 'calibration_error'] as const)
		: (['bleDisabled'] as const);
	$: flipKey = trvPriorityFlipKey(layout);
	$: displayCurrent =
		typeof currentC === 'number'
			? unit === 'C'
				? Number(currentC.toFixed(1))
				: celsius_to_fahrenheit(currentC)
			: undefined;
	$: displayTarget =
		typeof targetC === 'number'
			? unit === 'C'
				? Number(targetC.toFixed(1))
				: celsius_to_fahrenheit(targetC)
			: undefined;
	$: liveValue = enableTrv ? displayTarget : position;
	$: controlValue = sliderHeld ?? liveValue;
	$: min = enableTrv ? (unit === 'C' ? 4 : Math.round(celsius_to_fahrenheit(4))) : 0;
	$: max = enableTrv ? (unit === 'C' ? 30 : Math.round(celsius_to_fahrenheit(30))) : 100;
	$: step = enableTrv ? (unit === 'C' ? 0.5 : 1) : 1;
	$: dialMatchThreshold = enableTrv ? step : TRV_VALVE_SETTLED_DELTA;
	$: tempFavPositions = Array.isArray(device?.temp_fav_positions)
		? device.temp_fav_positions
		: defaultTempFavPositions(unit);
	$: activeTempFavs = tempFavPositions.filter((pos) => pos?.active);
	$: boostMinutesRaw = convertTime(trv?.config?.['trv:0']?.default_boost_duration ?? 900, 'seconds');
	$: boostMinutes =
		typeof boostMinutesRaw === 'number' ? Math.max(1, Math.round(boostMinutesRaw)) : 15;
	$: timer =
		is_gen_2(device) &&
		typeof status?.[get_gen_2_component_channel_structure(device)]?.timer_started_at !== 'undefined';
	$: bleDisabled = $settingsStore?.ble?.enable === false;
	$: statusLabel = resolveLabel(layout.statusLabel);
	$: controlDisabled = Boolean(layout.overlay);
	$: currentLabel =
		typeof displayCurrent === 'number'
			? $format('label_trv_current_meta', {
					default: 'Current {value}°',
					values: { value: displayCurrent },
				})
			: '';
	$: deviceID = real_id(device.id);

	let timerLabels: Record<string, unknown> = {};
	let waitForBoostResponse = false;
	let sendTimeout: ReturnType<typeof setTimeout>;
	let sliderHeld: number | undefined;
	let valveCommandSeq = 0;
	let pendingValvePosition: number | undefined;

	const holdDialValue = (next: number) => {
		sliderHeld = next;
	};

	const releaseDialValue = () => {
		sliderHeld = undefined;
	};

	$: if (sliderHeld != null && liveMatchesTrvDialValue(liveValue, sliderHeld, dialMatchThreshold)) {
		sliderHeld = undefined;
	}

	const valvePositionRequest = createSettlingRequestProxy(
		(pos: number) =>
			callBluTRV(
				device,
				'Trv.SetPosition',
				{
					id: 0,
					pos,
				},
				false,
			),
		{
			getExpected: (pos) => pos,
			isSettled: isTrvValveSettled,
			timeoutMs: TRV_VALVE_SETTLE_TIMEOUT_MS,
			stallMs: 0,
			waitAfterRpcFailure: true,
		},
	);

	const targetTemperatureRequest = createSettlingRequestProxy(
		(target_C: number) =>
			callBluTRV(
				device,
				'Trv.SetTarget',
				{
					id: 0,
					target_C,
				},
				false,
			),
		{
			getExpected: (target_C) => target_C,
			isSettled: isTrvValveSettled,
			timeoutMs: TRV_VALVE_SETTLE_TIMEOUT_MS,
			stallMs: 0,
			waitAfterRpcFailure: true,
		},
	);

	$: if (typeof position === 'number') {
		valvePositionRequest.observeLive(position);
	}

	$: if (typeof targetC === 'number') {
		targetTemperatureRequest.observeLive(targetC);
	}

	$: if (pendingValvePosition != null && isTrvValveSettled(position, pendingValvePosition)) {
		pendingValvePosition = undefined;
	}

	let flipHost: HTMLElement | undefined;
	let previousFlip = { rects: new Map<string, DOMRect>(), gridHeight: 0 };
	let playedFlipKey = '';
	let flipRaf = 0;

	const flipRoot = (): HTMLElement | undefined => {
		const root =
			flipHost?.closest('.sidepanel-content-wrapper') instanceof HTMLElement
				? flipHost.closest('.sidepanel-content-wrapper')
				: flipHost;
		return root instanceof HTMLElement ? root : undefined;
	};

	beforeUpdate(() => {
		const root = flipRoot();
		if (!root || flipKey === playedFlipKey) return;
		previousFlip = capturePropertyCardFlip(root);
	});

	afterUpdate(() => {
		const root = flipRoot();
		if (!root || flipKey === playedFlipKey) return;
		playedFlipKey = flipKey;
		const snapshot = previousFlip;
		previousFlip = { rects: new Map(), gridHeight: 0 };
		if (!snapshot.rects.size) return;
		if (flipRaf) cancelAnimationFrame(flipRaf);
		flipRaf = requestAnimationFrame(() => {
			flipRaf = 0;
			playPropertyCardFlip(root, snapshot);
		});
	});

	onDestroy(() => {
		if (flipRaf) cancelAnimationFrame(flipRaf);
		clearTimeout(sendTimeout);
		valvePositionRequest.abort();
		targetTemperatureRequest.abort();
	});

	const sendChange = async (value: number) => {
		const runId = ++valveCommandSeq;
		if (enableTrv) {
			valvePositionRequest.abort();
			const targetCelsius = unit === 'C' ? value : +fahrenheit_to_celsius(value).toFixed(1);
			const { outcome } = await targetTemperatureRequest(targetCelsius);
			if (runId !== valveCommandSeq) return;
			if (shouldRevertTrvDialOptimistic(outcome)) {
				releaseDialValue();
			}
			return;
		}
		targetTemperatureRequest.abort();
		const pos = Math.round(value);
		const { outcome } = await valvePositionRequest(pos);
		if (runId !== valveCommandSeq) return;
		if (shouldRevertTrvDialOptimistic(outcome)) {
			releaseDialValue();
		}
	};

	const onChange = (value: number) => {
		if (layout.overlay) return;
		const next = enableTrv ? value : Math.round(value);
		pendingValvePosition = undefined;
		holdDialValue(next);
		clearTimeout(sendTimeout);
		sendTimeout = setTimeout(() => sendChange(next), 1000);
		if ($os === 'IOS') {
			hapticFeedback('selection');
		}
	};

	const setBoost = async (nextBoost: boolean) => {
		waitForBoostResponse = true;
		if (nextBoost) {
			await callBluTRV(device, 'Trv.SetBoost', { id: 0 });
		} else {
			await callBluTRV(device, 'Trv.ClearBoost', { id: 0 });
		}
		waitForBoostResponse = false;
	};

	const setValvePosition = async (pos: number) => {
		if (layout.overlay) return;
		clearTimeout(sendTimeout);
		releaseDialValue();
		targetTemperatureRequest.abort();
		const runId = ++valveCommandSeq;
		pendingValvePosition = pos;
		const { outcome } = await valvePositionRequest(pos);
		if (runId !== valveCommandSeq) return;
		if (shouldClearTrvValvePending(outcome, position, pos)) {
			pendingValvePosition = undefined;
		}
	};

	const stopValve = () => {
		if (typeof position !== 'number') return;
		setValvePosition(Math.round(position));
	};

	const openCalibration = () => {
		navigateToSetting(device.id, 'settings', 'calibration', true, {
			initiate: true,
		});
	};

	const onCalibrationKeydown = (event: KeyboardEvent) => {
		if (event.key === 'Enter' || event.key === ' ') {
			event.preventDefault();
			openCalibration();
		}
	};

	$: a11yProps = {
		toggleButtonAriaLabel: $format('label_trv_radiator_status', { default: 'Radiator status' }),
		temperatureSliderAriaLabel: $format('label_set_target_temperature', {
			default: 'Set target temperature',
		}),
		positionSliderAriaLabel: $format('label_set_valve_position', {
			default: 'Set valve position',
		}),
	};
</script>

<DeviceViewStateWrapper
	{device}
	ignoreOfflineState
	hideSlotStaticTop
	hideSlotStaticBottom
	hideSlotFloatingBottom={!overlayCopy}
>
	<div class="trv-priority-view" slot="content" bind:this={flipHost}>
		<div class="label-list-wrapper mb-s d-flex flex-direction-column justify-content-center">
			{#if timer}
				{#key status?.[get_gen_2_control_component_channel_structure(device)]?.timer_started_at}
					<TimerRemaining timerKey="gen2_main" {device} {status} {settings} bind:timerLabels />
				{/key}
			{/if}
			<DeviceNotificationLabel deviceId={device?.id} excludeErrors={notificationExcludes} />
			{#if bleDisabled}
				<BluTrvBluetoothDisableAlert {deviceID} />
			{/if}
			{#if enableTrv && !device?.shared && activeTempFavs.length === 0}
				<Alert
					dismissable={true}
					type="info"
					isHeadingBold
					heading={$format('settings_title_favorites', {
						default: 'Favorite positions',
					})}
					message={$format('alert_info_heading_temp_fav_positions', {
						default:
							'You can enable or disable temperature favorite positions from sensor settings',
					})}
					cta={{
						title: $format('button_enable_disable', {
							default: 'Enable/Disable',
						}),
					}}
					on:cta_click={() => {
						navigateToSetting(
							device.id,
							'sensor_settings',
							'temperature_favorite_positions',
							false,
						);
					}}
				/>
			{/if}
		</div>
		{#if trvStatus}
			<TrvControl
				class="mb-s"
				value={controlValue ?? 0}
				disabled={controlDisabled}
				current={displayCurrent}
				{currentLabel}
				type={layout.mode}
				{min}
				{max}
				{step}
				{statusLabel}
				{a11yProps}
				on:value_change={(event) => onChange(event.detail.value)}
			/>

			{#if enableTrv && activeTempFavs.length > 0}
				<div
					class="trv-favorites mb-s"
					role="group"
					aria-label={$format('settings_title_favorites', { default: 'Favorite positions' })}
				>
					{#each tempFavPositions as pos, i}
						<IconButton
							id={`temp_fav_position_${pos.mode}_${i}`}
							aria-label={$format(`temp_fav_position_${pos.mode}`, {
								default: capFirstChar(pos.mode),
							})}
							title={$format(`temp_fav_position_${pos.mode}`, {
								default: capFirstChar(pos.mode),
							})}
							disabled={!pos?.active || controlDisabled}
							icon={getWorkingModeIcon(pos.mode)}
							variant={pos?.active && Number(controlValue) === Number(pos.value)
								? 'primary'
								: 'secondary'}
							on:click={() => onChange(Number(pos.value))}
						/>
					{/each}
				</div>
			{/if}

			{#if enableTrv}
				<ControlsContainer class="mb-s" color="color-0" size="s">
					<svelte:fragment slot="first">
						<div class="trv-boost-content">
							<DeviceButtonV2
								id="trv-boost-button"
								aria-label={boost
									? $format('label_cancel_boost', { default: 'Cancel Boost' })
									: $format('label_start_boost', { default: 'Start Boost' })}
								icon="far fa-rocket"
								variant={boost ? 'danger' : 'primary'}
								isActive={boost}
								isLoading={waitForBoostResponse}
								disabled={waitForBoostResponse || controlDisabled}
								size="m"
								on:click={() => setBoost(!boost)}
							/>
							<div class="trv-boost-copy">
								<Text class="text-bold"
									>{boost
										? $format('label_trv_boost_active', { default: 'Boost active' })
										: $format('sub_settings_title_boost', {
												default: 'Boost',
											})}</Text
								>
								<Text styleType="tiny" class="text-color-muted">
									{boost
										? $format('label_trv_boost_cancel_hint', {
												default: 'Tap to cancel',
											})
										: $format('label_trv_boost_duration_hint', {
												default: 'Heat quickly for {minutes} minutes',
												values: { minutes: boostMinutes },
											})}
								</Text>
							</div>
						</div>
					</svelte:fragment>
				</ControlsContainer>
			{:else}
				<div class="mb-s">
					<TrvValveCommandGroup
						disabled={Boolean(layout.overlay)}
						currentPosition={typeof position === 'number' ? position : undefined}
						pendingPosition={pendingValvePosition}
						onClose={() => setValvePosition(0)}
						onStop={stopValve}
						onOpen={() => setValvePosition(100)}
					/>
				</div>
			{/if}

			<PriorityGrid animate={false}>
				{#each layout.cards as card (card.id)}
					<PropertyCard
						data-property-id={card.id}
						priority={card.priority}
						value={card.value}
						unit={card.unit}
						label={resolveLabel(card.label)}
						icon={card.icon}
						tone={card.tone}
						stale={card.stale}
						disabled={Boolean(card.stale || layout.overlay)}
						kind={card.kind}
						accentColor={card.accentColor}
						hideCaption
					/>
				{/each}
				{#if layout.showNextSchedule}
					<NextScheduleCard
						{device}
						stale={Boolean(layout.overlay)}
						disabled={Boolean(layout.overlay)}
					/>
				{/if}
			</PriorityGrid>
		{:else}
			<Text class="mb-s text-bold text-center text-color-3 body-small-styles">
				{$format('text_blu_trv_fetching_data', {
					default: 'Fetching data...',
				})}
			</Text>
		{/if}
	</div>

	<svelte:fragment slot="floatingBottom">
		<PropertyDock
			hidden={!overlayCopy}
			role="status"
			label={overlayCopy
				? `${resolveLabel(overlayCopy.value)}. ${resolveLabel(overlayCopy.label)}`
				: $format('label_pinned_device_properties', { default: 'Pinned device properties' })}
		>
			{#if overlayCopy}
				<PropertyCard
					data-property-id="overlay"
					priority="primary"
					kind="status"
					tone={overlayCopy.tone}
					accentColor={overlayCopy.tone === 'muted' ? 'var(--color-theme-fg-02)' : undefined}
					value={resolveLabel(overlayCopy.value)}
					label={resolveLabel(overlayCopy.label)}
					icon={overlayCopy.icon}
				>
					<svelte:fragment slot="bottomRight">
						{#if overlayCta}
							<Text
								element="div"
								styleType="tiny"
								class="theme-text-accent link-underline"
								id="trv-calibrate-cta"
								role="button"
								tabindex="0"
								aria-label={$format('navigate_to_calibration', {
									default: 'Navigate to calibration',
								})}
								on:click={openCalibration}
								on:keydown={onCalibrationKeydown}
							>
								{capFirstChar($format(overlayCta.key, { default: overlayCta.default }))}
							</Text>
						{/if}
					</svelte:fragment>
				</PropertyCard>
			{/if}
		</PropertyDock>
	</svelte:fragment>
</DeviceViewStateWrapper>

<style>
	.trv-favorites {
		display: grid;
		grid-template-columns: repeat(4, minmax(0, 1fr));
		gap: var(--spacing-s);
		width: 100%;
	}

	.trv-favorites :global(.icon-button) {
		width: 100%;
		min-width: 0;
		height: 40px;
	}

	.trv-boost-content {
		display: flex;
		min-height: 60px;
		align-items: center;
		gap: var(--spacing-s-plus);
		padding-inline: var(--spacing-xs);
		text-align: left;
	}

	.trv-boost-copy {
		display: flex;
		min-width: 0;
		flex-direction: column;
		gap: var(--spacing-xxs);
	}

	:global(#trv-calibrate-cta:focus-visible) {
		text-decoration: underline;
	}
</style>
